import { Box, Card, Chip, Tooltip, Typography } from '@mui/material';
import { IServiceView } from '../../../services';
import { formatDuration } from '../../../helpers/formatDuration';
import { Button } from '../../MUI/button/Button';

interface IServiceCardProps {
  service: IServiceView;
  onSchedule: (service: IServiceView) => void;
  /** Layout compacto para grids (ex: PublicPage) — sem botão interno, clique no card todo */
  compact?: boolean;
  /** Serviço sem profissional disponível — exibe como indisponível */
  disabled?: boolean;
  /** Mensagem do tooltip quando disabled (opcional) */
  disabledTooltip?: string;
}

export const ServiceCard: React.FC<IServiceCardProps> = ({ service, onSchedule, compact, disabled, disabledTooltip }) => {
  const cardContent = (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        display: 'flex',
        flexDirection: compact ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: compact ? 'stretch' : 'center',
        gap: compact ? 1 : 0,
        transition: 'transform 0.2s, box-shadow 0.2s',
        ...(disabled ? { opacity: 0.55 } : {}),
        '&:hover': compact && !disabled ? { transform: 'translateY(-2px)' } : {},
      }}
    >
      <Box flex={1}>
        <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
          <Typography variant='body1' fontWeight={600}
            sx={disabled ? { color: 'text.disabled' } : {}}
          >
            {service.description}
          </Typography>
          {service.requiresPreApproval && (
            <Chip
              label='Requer confirmação'
              color='warning'
              size='small'
              variant='outlined'
              sx={{ fontWeight: 500 }}
            />
          )}
          {disabled && (
            <Chip
              label='Indisponível'
              color='default'
              size='small'
              variant='outlined'
              sx={{ fontWeight: 500, borderStyle: 'dashed' }}
            />
          )}
        </Box>
        <Box display='flex' gap={1.5} mt={0.5}>
          <Typography variant='body2' fontWeight={600}
            sx={disabled ? { color: 'text.disabled' } : { color: 'primary.main' }}
          >
            R$ {service.amount.toFixed(2).replace('.', ',')}
          </Typography>
          {service.serviceDuration && (
            <Typography variant='body2' color='text.secondary'>
              {formatDuration(service.serviceDuration)}
            </Typography>
          )}
        </Box>
      </Box>

      {!compact && (
        <Button
          label='Agendar'
          variant='contained'
          size='small'
          disabled={disabled}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (!disabled) onSchedule(service);
          }}
          data-testid={`btn-schedule-service-${service.id}`}
        />
      )}
    </Box>
  );

  const wrappedContent = disabled && disabledTooltip ? (
    <Tooltip title={disabledTooltip} arrow>
      <Box>{cardContent}</Box>
    </Tooltip>
  ) : cardContent;

  if (compact) {
    return (
      <Card
        sx={{
          borderRadius: 2,
          cursor: disabled ? 'default' : 'pointer',
          ...(disabled ? { opacity: 0.7 } : {}),
          '&:hover': disabled ? {} : { boxShadow: 4 },
        }}
        onClick={() => { if (!disabled) onSchedule(service); }}
      >
        {wrappedContent}
      </Card>
    );
  }

  return (
    <Card
      variant='outlined'
      sx={{
        borderRadius: 2,
        bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
      }}
    >
      {wrappedContent}
    </Card>
  );
};
